import { Box, Flex, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { CREATIVE_LANGUAGE_CODES, isYouTubeEmbedUrl } from "../../api";
import { OptimizeGame } from "../../data";
import {
  parseGameId,
  previewPool,
  useApp,
  useCreatives,
  useOptimizeLive,
} from "../../liveStore";
import {
  OfferMedia,
  MCoinChip,
  MCollectableRow,
  MCollectables,
  MCountdown,
  MDurationTag,
  MGameIcon,
  MGameTiles,
  MOfferCard,
  MProgress,
  MSectionCard,
  MTaskCard,
  PreviewSurface,
  TM,
} from "./primitives";
import { CoinAnimatedIcon, DiamondIcon } from "./MLottie";

// How each optimization looks inside the PlayMAX app, per Günay's wireframe.
// `game` is the partner's marked game (or a random one when nothing is marked).
// `detail` marks the detail-page variant — only the creatives preview differs,
// showing its language switcher there (removed from the overview per Günay).
type PreviewProps = { game: OptimizeGame | null; detail?: boolean };

// partner's own titles first (marked game leading), placeholder slots after.
// previewPool has one entry per platform, but the app shows a title once — so
// dedupe by name and keep the first (marked) platform entry.
const others = (game: OptimizeGame | null, count: number): (OptimizeGame | null)[] => {
  const seen = new Set<string>();
  const picked: (OptimizeGame | null)[] = [];
  for (const g of [game, ...previewPool()]) {
    if (!g || seen.has(g.name) || picked.length >= count) continue;
    seen.add(g.name);
    picked.push(g);
  }
  while (picked.length < count) picked.push(null);
  return picked;
};

// the two top-row cards keep their natural height (fill={false}): stretched to
// the creative card beside them they carried empty space (Günay's feedback)
const RecurringEventsPreview: React.FC<{ game: OptimizeGame | null }> = ({
  game,
}) => (
  <MTaskCard game={game} showStatus fill={false}>
    <MProgress title="Complete Level 5" value={60} label="3 / 5 Levels Complete" />
    <MProgress title="Complete Level 10" value={30} label="3 / 10 Levels Complete" />
    <MProgress title="Complete Level 20" value={15} label="3 / 20 Levels Complete" />
  </MTaskCard>
);

const RewardedAdEventsPreview: React.FC<{ game: OptimizeGame | null }> = ({
  game,
}) => (
  <MTaskCard game={game} showStatus fill={false}>
    <MProgress title="Watch 5 Rewarded Ads" value={80} label="4 / 5 Ads Watched" />
    <MProgress title="Watch 20 Rewarded Ads" value={20} label="4 / 20 Ads Watched" />
    <MProgress title="Watch 50 Rewarded Ads" value={8} label="4 / 50 Ads Watched" />
  </MTaskCard>
);

// The German chrome of the overview's second card. "TOP" is the same word in
// German, so only the CTA actually changes.
const DE_CTA = "SPIELEN & VERDIENEN";

// The creative is the card's banner, so this shows the whole "New" tab offer
// card with the real creative, resolved per CREATIVES_FRONTEND_GUIDE.md §4:
// override video → override banner → store video (/apps) → store banner →
// gradient placeholder. The overview shows an English card plus a partially
// visible German one beside it (Günay: the cut-off hints there's a card per
// language); the detail page shows one card with the language picker instead.
const OverrideCreativesPreview: React.FC<PreviewProps> = ({
  game,
  detail,
}) => {
  const [languageIndex, setLanguageIndex] = useState(0);
  const gameId = game?.id ?? "";
  const app = useApp(gameId); // store defaults live on /apps
  const { creatives } = useCreatives(gameId); // partner overrides
  const loadCreatives = useOptimizeLive((s) => s.loadCreatives);

  useEffect(() => {
    const parsed = game ? parseGameId(game.id) : null;
    if (parsed) loadCreatives(parsed.appId, parsed.platform);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  // the §4 chain for one language: its override, else the store creative
  const resolveMedia = (langCode: string): OfferMedia | null => {
    const override = (kind: "video" | "banner") =>
      creatives?.find(
        (c) => c.languageCode === langCode && c.type === kind
      );
    const best = override("video") ?? override("banner");
    if (best)
      // override urls are always real media files — no embed check needed
      return { kind: best.type === "video" ? "video" : "image", url: best.url };
    if (app?.videoUrl)
      return {
        kind: isYouTubeEmbedUrl(app.videoUrl) ? "embed" : "video",
        url: app.videoUrl,
      };
    if (app?.bannerImageUrl)
      return { kind: "image", url: app.bannerImageUrl };
    return null;
  };

  const language = detail
    ? CREATIVE_LANGUAGE_CODES[languageIndex]
    : CREATIVE_LANGUAGE_CODES[0];
  const media = resolveMedia(language.code);
  const hasOverride = creatives?.some(
    (c) => c.languageCode === language.code
  );

  // what the selected language would actually serve, for the caption below
  const source = !game
    ? null
    : hasOverride
    ? "your uploaded override"
    : media
    ? "store creative (no override uploaded)"
    : "nothing on file — placeholder shown";

  const title = app?.appName ?? undefined;

  return (
    // on the overview the column fills its grid cell, so the white-ish
    // rectangle runs exactly as tall as the row and the three cards line up
    // (Günay: "make height of the whole row the same")
    <Flex direction="column" h={detail ? undefined : "100%"}>
      {/* the offer card renders smaller, as app UI sitting inside a rectangle
          in the same white-ish finish as the other cards (Günay's feedback —
          the earlier screen-grey surface stood out against its row neighbours) */}
      <Flex
        flex={detail ? undefined : "1"}
        align="center"
        justify={detail ? "center" : "flex-start"}
        background={TM.cardGradient}
        border={`0.5px solid ${TM.cardBorder}`}
        borderRadius="8.52px"
        p="14px"
        // the strip is wider than the rectangle on purpose: the German card
        // gets cut at the edge, and the row scrolls for whoever wants to see it
        overflowX={detail ? undefined : "auto"}
        sx={detail ? undefined : { "&::-webkit-scrollbar": { display: "none" } }}
      >
        {detail ? (
          /* narrow enough that the card's derived height stays under the task
             cards' row height — the wrapper then pads it to an equal row */
          <Box w="100%" maxW="250px">
            {/* the full store title, not the shortened display name — the
                offer card truncates it on one line (Günay) */}
            <MOfferCard game={game} title={title} media={media} />
          </Box>
        ) : (
          <Flex gap="14px" align="center">
            <Box w="250px" flexShrink={0}>
              <MOfferCard
                game={game}
                title={title}
                media={resolveMedia("en")}
              />
            </Box>
            {/* the German slot: its own override when uploaded, otherwise the
                same store default — with the app chrome auf Deutsch */}
            <Box w="250px" flexShrink={0}>
              <MOfferCard
                game={game}
                title={title}
                media={resolveMedia("de")}
                cta={DE_CTA}
              />
            </Box>
          </Flex>
        )}
      </Flex>
      {detail && (
        <>
          <Flex wrap="wrap" justify="center" gap="6px" mt="10px">
            {CREATIVE_LANGUAGE_CODES.map((lang, index) => {
              const active = index === languageIndex;
              const hasOverride = creatives?.some(
                (c) => c.languageCode === lang.code
              );
              return (
                <Flex
                  key={lang.code}
                  as="button"
                  align="center"
                  onClick={() => setLanguageIndex(index)}
                  px="10px"
                  py="3px"
                  borderRadius="full"
                  fontSize="12px"
                  fontWeight="600"
                  bg={active ? TM.text : "transparent"}
                  color={active ? "#fff" : TM.textSecondary}
                  border={`1px solid ${
                    active ? TM.text : "rgba(40,45,89,0.25)"
                  }`}
                >
                  {lang.label}
                  {hasOverride && (
                    <Box
                      w="5px"
                      h="5px"
                      borderRadius="full"
                      bg={TM.green}
                      ml="4px"
                    />
                  )}
                </Flex>
              );
            })}
          </Flex>
          {source && (
            <Text
              color={TM.textSecondary}
              fontSize="12px"
              textAlign="center"
              mt="6px"
            >
              {language.label}: {source}
            </Text>
          )}
        </>
      )}
    </Flex>
  );
};

// the three streaks run in parallel off the same play history, so a user two
// days in reads 2 / 3, 2 / 7 and 2 / 30 — not a different count per card
const RetentionPreview: React.FC<{ game: OptimizeGame | null }> = ({ game }) => (
  <MTaskCard game={game} showStatus>
    <MProgress
      title="Play daily for 3 days"
      value={67}
      label="2 / 3 Days"
      leading={<MCoinChip amount="100" />}
      rewardChip={<MDurationTag label="2/3D" />}
    />
    <MProgress
      title="Play daily for 7 days"
      value={29}
      label="2 / 7 Days"
      leading={<MCoinChip amount="1,000" />}
      rewardChip={<MDurationTag label="2/7D" />}
    />
    <MProgress
      title="Play daily for 30 days"
      value={7}
      label="2 / 30 Days"
      leading={<MCoinChip amount="10,000" />}
      rewardChip={<MDurationTag label="2/30D" />}
    />
  </MTaskCard>
);

const CollectablesPlaytimePreview: React.FC<{ game: OptimizeGame | null }> = ({
  game,
}) => {
  // two rows only: the zig-zag stepper is taller than the old single line and
  // Günay okayed dropping the 3rd game to make room
  const [first, second] = others(game, 2);
  return (
    <MSectionCard title="Collect 10 🐶 to receive the reward">
      <MCollectables emoji="🐶" total={10} collected={3} />
      <MCollectableRow
        game={first}
        appName={first?.name ?? "App name"}
        task="Play 15 minutes"
        count={1}
        emoji="🐶"
      />
      <MCollectableRow
        game={second}
        appName={second?.name ?? "App name 2"}
        task="Play 15 minutes"
        count={1}
        emoji="🐶"
      />
    </MSectionCard>
  );
};

// Once the title has real challenges saved, the preview shows those instead of
// the pre-filled samples (Günay's feedback) — with a plausible mid-way progress,
// since actual user progress isn't a thing the dashboard can know.
const TimeLimitedPreview: React.FC<{ game: OptimizeGame | null }> = ({
  game,
}) => {
  // the board is only fetched once the title's setup panel has been opened;
  // until then the samples show
  const board = useOptimizeLive((s) =>
    game ? s.boards[game.id] : undefined
  );
  const configured =
    board?.cards
      .find((c) => c.key === "time_limited_challenges")
      ?.entries.filter((entry) => entry.params) ?? [];

  if (configured.length > 0) {
    return (
      <MTaskCard game={game} showStatus>
        {configured.map((entry) => {
          const params = entry.params!;
          const count =
            params.eventType === "recurring" ? params.eventCount : undefined;
          const done = count ? Math.max(1, Math.round(count * 0.4)) : undefined;
          return (
            <MProgress
              key={entry.slot}
              title={params.taskName}
              value={count ? (done! / count) * 100 : 40}
              label={count ? `${done} / ${count}` : undefined}
              rewardChip={<MDurationTag label={`${params.timeLimitDays}D`} />}
            />
          );
        })}
      </MTaskCard>
    );
  }

  return (
    <MTaskCard game={game} showStatus>
      <MProgress
        title="Finish Level 20 within 3 Days"
        value={80}
        label="Level 16 / 20"
        rewardChip={<MDurationTag label="3D" />}
      />
      <MProgress
        title="Finish Level 100 within 7 Days"
        value={16}
        label="Level 16 / 100"
        rewardChip={<MDurationTag label="7D" />}
      />
      <MProgress
        title="Finish Level 250 within 30 Days"
        value={6}
        label="Level 16 / 250"
        rewardChip={<MDurationTag label="30D" />}
      />
    </MTaskCard>
  );
};

const DailyPlaytimePreview: React.FC<{ game: OptimizeGame | null }> = ({
  game,
}) => (
  <MSectionCard title="Play each of those games for 5 minutes">
    <MGameTiles
      games={others(game, 3)}
      progress={[60, 20, 0]}
      statusIndex={0}
      newIndex={2}
    />
  </MSectionCard>
);

const DailyRewardedAdsPreview: React.FC<{ game: OptimizeGame | null }> = ({
  game,
}) => (
  <MSectionCard title="Watch 5 rewarded ads across games below">
    <MGameTiles games={others(game, 3)} statusIndex={0} />
    <MProgress value={80} label="4 / 5 Ads Watched" />
  </MSectionCard>
);

// Per Günay's wireframe: icon/name/PLAY row, a red countdown, the challenge
// title, then a progress bar labeled with the purchase count.
const PurchasePreview: React.FC<{ game: OptimizeGame | null }> = ({ game }) => (
  <MTaskCard game={game} showStatus>
    <MCountdown time="Expires in 00:12:54" />
    <Text color={TM.text} fontWeight="500" fontSize="13.1px" textAlign="center" mb="2px">
      Make 3 purchases within 3 days
    </Text>
    <MProgress value={67} label="2 / 3 Purchases Made" />
  </MTaskCard>
);

// Burning Rewards is its own self-contained card, not a header on a task card:
// moneycash_android/components/screens/Tasks/components/BurningRewards/View.tsx.
// Orange→red gradient header, big red HH:MM:SS, the challenge row, then the base
// reward struck out with a red X beside the boosted coin/diamond lines.
const BR = {
  red: "#F2003C",
  orange: "#ff7b35",
  border: "#ffb27a",
  dark: "#1a1a2e",
  muted: "#8c8ca0",
};

const BurningRewardsPreview: React.FC<{ game: OptimizeGame | null }> = ({
  game,
}) => (
  // the app's wrapper padding (deviceHeight/120 top, deviceWidth/14 bottom) only
  // separates it from the cards around it — dead space in a one-card preview
  <Box h="100%">
    <Box
      borderRadius="7.1px"
      bg="#fff"
      boxShadow="0 6px 14px rgba(255,123,53,0.18)"
      h="100%"
    >
      <Box
        borderRadius="7.1px"
        border={`1.5px solid ${BR.border}`}
        bg="#fff"
        overflow="hidden"
        h="100%"
        display="flex"
        flexDirection="column"
      >
        {/* RN's start{0,0}→end{1,1} is a literal corner-to-corner axis, which on
            a ~43px-tall header is almost horizontal — not CSS's 135deg 45° axis */}
        <Flex
          position="relative"
          align="center"
          justify="center"
          py="7px"
          background="linear-gradient(98.6deg, #FFA62B 0%, #FF6A2B 50%, #F2003C 100%)"
          overflow="hidden"
        >
          {/* ButtonShimmer: full-bleed wash, 1800ms eased sweep across ±100%,
              then a 1400ms hold (3200ms cycle) */}
          <Box
            position="absolute"
            inset="0"
            pointerEvents="none"
            background="linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)"
            sx={{
              animation: "brShimmer 3.2s linear infinite",
              "@keyframes brShimmer": {
                "0%": {
                  transform: "translateX(-100%)",
                  animationTimingFunction: "ease-in-out",
                },
                "56.25%": { transform: "translateX(100%)" },
                "100%": { transform: "translateX(100%)" },
              },
            }}
          />
          <Text
            color="#fff"
            fontWeight="700"
            fontSize="20.3px"
            textAlign="center"
            textShadow="0 1px 2px rgba(0,0,0,0.25)"
          >
            Burning Rewards
          </Text>
        </Flex>

        {/* body paddingHorizontal is CARD_WIDTH * 0.05; percentage padding
            resolves against the parent's width, so this stays proportional
            now that the card fills the preview instead of a fixed 357px */}
        <Box px="5%" py="10px">
          <Text
            color={BR.red}
            fontWeight="700"
            fontSize="28px"
            textAlign="center"
            letterSpacing="1px"
            mb="10px"
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            23:59:03
          </Text>

          <Flex align="center" justify="space-between">
            <Box
              w="48px"
              h="48px"
              borderRadius="10px"
              border={`1.5px solid ${BR.border}`}
              bg="#f2ebe1"
              overflow="hidden"
              flexShrink={0}
            >
              {/* RN borders are inset, so gameIcon's 100% is the content box */}
              <MGameIcon game={game} size="100%" radius="0" />
            </Box>
            <Box flex="1" ml="13.76px" mr="7.86px" minW="0">
              <Text
                color={BR.dark}
                fontWeight="700"
                fontSize="16.4px"
                noOfLines={2}
              >
                Play 100 Matches
              </Text>
              <Text
                color={BR.muted}
                fontWeight="500"
                fontSize="12.2px"
                mt="1.42px"
                noOfLines={1}
              >
                {game?.name ?? "App name"}
              </Text>
            </Box>
            <Flex
              bg={BR.orange}
              px="18px"
              py="7px"
              borderRadius="14px"
              boxShadow="0 3px 8px rgba(255,123,53,0.4)"
              flexShrink={0}
            >
              <Text
                color="#fff"
                fontWeight="700"
                fontSize="16.4px"
                letterSpacing="1px"
              >
                PLAY
              </Text>
            </Flex>
          </Flex>

          {/* rewardRow paddingHorizontal is CARD_WIDTH * 0.06, but % resolves
              against the body's content box, which the body's own 5%/side
              already narrowed — hence 6 / 0.9 */}
          <Flex
            align="center"
            justify="space-between"
            mt="10px"
            px="6.667%"
            wrap="wrap"
            rowGap="6px"
          >
            {/* base reward with two 3px bars crossed corner-to-corner */}
            <Flex align="center" position="relative">
              <CoinAnimatedIcon size={26.6} />
              <Text
                color={BR.muted}
                fontWeight="700"
                fontSize="21.3px"
                ml="3.93px"
              >
                2150
              </Text>
              <Box
                as="svg"
                position="absolute"
                inset="0"
                w="100%"
                h="100%"
                pointerEvents="none"
                sx={{ overflow: "visible" }}
              >
                <line
                  x1="0"
                  y1="0"
                  x2="100%"
                  y2="100%"
                  stroke={BR.red}
                  strokeWidth="3"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
                <line
                  x1="0"
                  y1="100%"
                  x2="100%"
                  y2="0"
                  stroke={BR.red}
                  strokeWidth="3"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              </Box>
            </Flex>

            <Flex direction="column" align="flex-start" gap="3.87px">
              <Flex align="center">
                <Text
                  color={BR.dark}
                  fontWeight="700"
                  fontSize="17px"
                  mr="1.97px"
                >
                  +
                </Text>
                <CoinAnimatedIcon size={26.6} />
                <Text
                  color={BR.dark}
                  fontWeight="700"
                  fontSize="18.5px"
                  ml="1.97px"
                >
                  6450
                </Text>
                <Text fontSize="15.5px" ml="3.93px">
                  🔥
                </Text>
              </Flex>
              <Flex align="center">
                <Text
                  color={BR.dark}
                  fontWeight="700"
                  fontSize="17px"
                  mr="1.97px"
                >
                  +
                </Text>
                <DiamondIcon size={26.6} />
                <Text
                  color={BR.dark}
                  fontWeight="700"
                  fontSize="18.5px"
                  ml="1.97px"
                >
                  1000
                </Text>
                <Text fontSize="15.5px" ml="3.93px">
                  🔥
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Box>
      </Box>
    </Box>
  </Box>
);

const MultiGamePurchasePreview: React.FC<{ game: OptimizeGame | null }> = ({
  game,
}) => (
  // this card sits next to taller neighbours, so it fills its cell and centres
  // the icon list in the leftover height — icons stacked at the top left the
  // bottom looking empty (Günay's feedback)
  <MSectionCard fill>
    <Text color={TM.text} fontWeight="700" fontSize="15.5px">
      Make 3 Purchases
    </Text>
    <Text
      color={TM.textSecondary}
      fontSize="13.1px"
      fontWeight="500"
      mb="8px"
    >
      across any eligible games
    </Text>
    <MProgress value={67} label="2 / 3 Purchases Made" />
    <Flex flex="1" align="center">
      <Box w="100%">
        <MGameTiles games={others(game, 5)} statusIndex={0} newIndex={4} />
      </Box>
    </Flex>
  </MSectionCard>
);

const CollectablesEventsPreview: React.FC<{ game: OptimizeGame | null }> = ({
  game,
}) => {
  const [first, second] = others(game, 2);
  return (
    <MSectionCard title="Collect 7 🥐 to receive the reward">
      <MCollectables emoji="🥐" total={7} collected={3} />
      <MCollectableRow
        game={first}
        appName={first?.name ?? "App name"}
        task="Finish Level 10"
        count={1}
        emoji="🥐"
      />
      <MCollectableRow
        game={second}
        appName={second?.name ?? "App name 2"}
        task="Build Kingdom 3"
        count={1}
        emoji="🥐"
      />
    </MSectionCard>
  );
};

const PREVIEWS: Record<string, React.FC<PreviewProps>> = {
  "recurring_event_structure": RecurringEventsPreview,
  "rewarded_ad_events": RewardedAdEventsPreview,
  "override_creatives": OverrideCreativesPreview,
  "retention_challenges": RetentionPreview,
  "live_ops_collectables_playtime": CollectablesPlaytimePreview,
  "time_limited_challenges": TimeLimitedPreview,
  "daily_playtime_challenges": DailyPlaytimePreview,
  "daily_rewarded_ad_challenges": DailyRewardedAdsPreview,
  purchase_challenges: PurchasePreview,
  "burning_rewards": BurningRewardsPreview,
  "multi_game_purchase_challenges": MultiGamePurchasePreview,
  "live_ops_collectables_events": CollectablesEventsPreview,
};

const CardPreview: React.FC<{
  cardId: string;
  game: OptimizeGame | null;
  detail?: boolean;
}> = ({ cardId, game, detail }) => {
  const Preview = PREVIEWS[cardId];
  if (!Preview) return null;
  return (
    <PreviewSurface>
      <Preview game={game} detail={detail} />
    </PreviewSurface>
  );
};

export default CardPreview;
